import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/etsy-zu-shopify";
const TITLE = "Von Etsy zum eigenen Shopify-Shop: Ablauf und Kosten";
const DESCRIPTION =
  "Von Etsy zu Shopify wechseln oder parallel verkaufen: Produktübernahme, Kundenaufbau ohne Marktplatz, Gebührenvergleich und realistischer Zeit- und Kostenrahmen.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Der Wechsel von Etsy zu Shopify bedeutet vor allem, Reichweite selbst aufzubauen. Produkte lassen sich per CSV übernehmen, der eigentliche Aufwand liegt in Sichtbarkeit, Vertrauen und wiederkehrenden Kunden. Sinnvoll ist meist ein paralleler Betrieb: Etsy bleibt Reichweitenkanal, Shopify wird der Shop mit eigener Kundenbeziehung.";

const faqs = [
  {
    q: "Kann ich meine Etsy-Artikel zu Shopify übertragen?",
    a: "Ja, über einen CSV-Export der Artikel und den Shopify-Import. Bilder, Varianten und Beschreibungen sollten nach dem Import geprüft und für die eigene Seite überarbeitet werden, weil Etsy-Texte auf die Marktplatzsuche zugeschnitten sind.",
  },
  {
    q: "Soll ich Etsy aufgeben, wenn ich einen Shopify-Shop habe?",
    a: "In den meisten Fällen nicht sofort. Etsy liefert Reichweite, Shopify liefert Marge und Kundendaten. Viele Händler fahren beides parallel und verlagern Stammkunden schrittweise in den eigenen Shop.",
  },
  {
    q: "Lohnt sich Shopify gegenüber den Etsy-Gebühren?",
    a: "Das hängt vom Umsatz ab. Etsy-Gebühren fallen pro Verkauf an, Shopify kostet eine feste monatliche Gebühr plus Zahlungsgebühren. Ab einem stabilen monatlichen Umsatz rechnet sich der eigene Shop meist, aber nur mit eigener Reichweite.",
  },
  {
    q: "Was kostet der Aufbau eines Shopify-Shops für Etsy-Verkäufer?",
    a: "Ein sauberer Start liegt häufig bei 2.500 bis 6.000 € netto inklusive Theme, Datenübernahme und Zahlungsarten. Entwicklung berechne ich mit 110 € netto pro Stunde.",
  },
  {
    q: "Wie bekomme ich ohne Marktplatz Besucher?",
    a: "Über Inhalte zu Ihren Produkten, Suchmaschinenoptimierung, E-Mail-Marketing an bestehende Käufer, Social Media und gegebenenfalls Werbung. Das ist die eigentliche Arbeit nach dem Umzug, nicht die Technik.",
  },
];

export const Route = createFileRoute("/ratgeber/etsy-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Marktplatz"
      title="Etsy zu Shopify: vom Marktplatz zur eigenen Kundenbeziehung"
      intro="Auf Etsy verkaufen Sie in fremder Reichweite und mit fremden Regeln. Ein eigener Shopify-Shop dreht das um: mehr Marge, eigene Kundendaten, aber auch die Pflicht, Besucher selbst zu holen."
      source="ratgeber-etsy-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Was sich ändert",
          p: [
            "Auf Etsy kommt der Besucher von der Plattform. Im eigenen Shop kommt er von Ihnen: über Suchmaschinen, Inhalte, E-Mail und Social Media. Das ist der eigentliche Unterschied, nicht die Technik.",
          ],
        },
        {
          h: "Der pragmatische Weg",
          p: [],
          list: [
            "Shopify-Shop aufbauen und Artikel per CSV übernehmen",
            "Produkttexte von Marktplatzsprache auf eigene Marke umschreiben",
            "Etsy vorerst weiterlaufen lassen als Reichweitenkanal",
            "Bestandskunden per E-Mail in den eigenen Shop holen",
            "Inhalte zu Ihren Produkten aufbauen, damit Google Sie findet",
          ],
        },
        {
          h: "Gebühren realistisch rechnen",
          p: [
            "Etsy kostet pro Verkauf, Shopify kostet monatlich plus Zahlungsgebühren. Rechnen Sie beides auf Ihren tatsächlichen Monatsumsatz um, statt Prozentsätze zu vergleichen. Der eigene Shop gewinnt erst, wenn Sie eigene Besucher haben.",
          ],
        },
        {
          h: "Kosten und Zeitrahmen",
          p: [
            "Ein sauberer Start liegt häufig bei 2.500 bis 6.000 € netto und zwei bis vier Wochen. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shopify Shop erstellen lassen" },
        { to: "/blog/shopify-seo-optimierung-guide", label: "Shopify SEO Guide" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten" },
        { to: "/kontakt", label: "Projekt anfragen" },
      ]}
    />
  );
}
